import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Calendar, GraduationCap, ChevronRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const STATUS_TONE = {
  SCHEDULED: 'bg-[#1B3A87]/15 text-[#93C5FD]',
  PENDING_PAYMENT: 'bg-[#B45309]/15 text-[#FBBF24]',
  PAID: 'bg-[#167A5A]/15 text-[#7DD3A8]',
  IN_PROGRESS: 'bg-[#1B3A87]/15 text-[#93C5FD]',
  COMPLETED: 'bg-slate-700 text-slate-600',
  CANCELLED: 'bg-[#B42318]/15 text-[#FCA5A5]',
};

export default function TrainingBookings() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { api.get('/training/bookings').then(r => setRows(r.data)); }, []);
  const filtered = rows.filter(r => !q || [r.dog_name, r.client_name, r.trainer_name, r.program_label].some(x => (x||'').toLowerCase().includes(q.toLowerCase())));
  const now = new Date();
  const upcoming = filtered.filter(r => new Date(r.end) >= now && r.status !== 'CANCELLED');
  const past = filtered.filter(r => new Date(r.end) < now || r.status === 'CANCELLED');

  const Row = ({ r }) => (
    <Card className='p-4 bg-white border-slate-200 hover:border-[#1B3A87] transition-colors' data-testid={`tb-row-${r.id}`}>
      <div className='flex flex-wrap items-center gap-4 justify-between'>
        <div className='flex items-center gap-3 flex-1 min-w-[260px]'>
          <div className='w-10 h-10 rounded-lg bg-[#C8102E]/15 text-[#C8102E] flex items-center justify-center'><GraduationCap className='w-5 h-5' /></div>
          <div className='min-w-0'>
            <div className='font-semibold text-slate-900 truncate'>{r.dog_name} <span className='text-slate-500 text-sm'>· {r.client_name}</span></div>
            <div className='text-xs text-slate-500 truncate'>{r.program_label} · Trainer: <span className='text-slate-900'>{r.trainer_name}</span>{r.run_name ? <> · Run: <span className='font-mono'>{r.run_name}</span></> : null}</div>
          </div>
        </div>
        <div className='text-xs text-slate-500'>{new Date(r.start).toLocaleDateString()} → {new Date(r.end).toLocaleDateString()}</div>
        <div className='text-right'>
          <div className='font-display font-bold tabular-nums text-slate-900'>${r.amount.toFixed(2)}</div>
          <Badge className={`${STATUS_TONE[r.status] || ''} mt-1`}>{r.status.replace('_',' ')}</Badge>
        </div>
      </div>
    </Card>
  );

  return (
    <div className='p-6 lg:p-8'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Board & Train</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 mt-1'>Training Bookings</h1>
        </div>
        <Input placeholder='Search dog, client, trainer…' value={q} onChange={(e)=>setQ(e.target.value)} className='w-80 bg-white border-slate-200 text-slate-900' data-testid='tb-search-input' />
      </div>
      <Tabs defaultValue='upcoming'>
        <TabsList className='bg-white border border-slate-200 mb-3'>
          <TabsTrigger value='upcoming' data-testid='tb-tab-upcoming'>Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value='past' data-testid='tb-tab-past'>Past / Cancelled ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value='upcoming'>
          <div className='space-y-2'>
            {upcoming.length === 0 ? <Card className='p-8 text-center bg-white border-slate-200 text-slate-500'>No upcoming training bookings.</Card> : upcoming.map(r => <Row key={r.id} r={r} />)}
          </div>
        </TabsContent>
        <TabsContent value='past'>
          <div className='space-y-2'>
            {past.length === 0 ? <Card className='p-8 text-center bg-white border-slate-200 text-slate-500'>None yet.</Card> : past.map(r => <Row key={r.id} r={r} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
